import React, { useState, useEffect, useMemo } from 'react';
import { useGame } from '../context/GameContext';
import api from '../services/api';
import './GameInterface.css';

function GameInterface({ section, sectionTitle }) {
  const {
    selectedTeam,
    currentSession,
    submitAnswer,
    requestAudioReplay,
    requestHelp,
    submitDiningRoomQuestion4,
    socket
  } = useGame();

  const [questions, setQuestions] = useState([]);
  const [currentCabinet, setCurrentCabinet] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [audioPlayCount, setAudioPlayCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [diningRoomCompleted, setDiningRoomCompleted] = useState(false);
  const [audioPlayed, setAudioPlayed] = useState(false); // Track if the single audio has been played
  const [isAudioPlaying, setIsAudioPlaying] = useState(false); // Track if audio is currently playing
  // Audio URLs for dining room
  const DINING_ROOM_PLAY_AUDIO_URL = '/audio/valmont-2.0.mp3'; // First play audio
  const DINING_ROOM_REPLAY_AUDIO_URL = '/audio/valmont-2.1.mp3'; // Replay audio

  // Fetch questions for this section
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await api.get(`/question/${section}`);
        if (response.data.success) {
          // Sort questions by order to ensure correct sequence
          const sortedQuestions = [...response.data.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
          setQuestions(sortedQuestions);
        }
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };

    fetchQuestions();
  }, [section]);

  // Get current team score
  useEffect(() => {
    if (currentSession && selectedTeam) {
      const teamProgress = currentSession.teamProgress?.find(
        tp => tp.teamId.toString() === selectedTeam.id
      );
      if (teamProgress) {
        setScore(teamProgress.sections[section]?.score || 0);
        if (section === 'dining-room') {
          setAudioPlayCount(teamProgress.sections[section]?.audioPlayCount || 0);
          // Set audio played status based on play count (single audio)
          setAudioPlayed((teamProgress.sections[section]?.audioPlayCount || 0) > 0);
        }
      }
    }
  }, [currentSession, selectedTeam, section]);

  const handleCabinetSelect = (cabinet) => {
    const question = questions.find(q => q.cabinet === cabinet);
    if (question) {
      // Check if this question has already been answered
      if (currentSession && selectedTeam) {
        const teamProgress = currentSession.teamProgress?.find(
          tp => tp.teamId.toString() === selectedTeam.id
        );
        if (teamProgress) {
          const sectionProgress = teamProgress.sections[section];
          const alreadyAnswered = sectionProgress?.answeredQuestions?.includes(question._id);
          if (alreadyAnswered) {
            // Don't allow selecting already answered cabinets
            setFeedback({ type: 'error', message: 'This question has already been answered' });
            setTimeout(() => setFeedback(null), 2000);
            return;
          }
        }
      }

      setCurrentCabinet(cabinet);
      setCurrentQuestion(question);
      setAnswer('');
      setFeedback(null);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!answer.trim() || !currentQuestion) return;
    // Special handling for dining room question 4 (order 4)
    if (section === 'dining-room' && currentQuestion.order === 4) {
      setLoading(true);
      // Emit special event for question 4 to dashboard
      const result = await submitDiningRoomQuestion4(answer);
      setLoading(false);

      if (result.error) {
        setFeedback({ type: 'error', message: result.error });
        return;
      }

      // For question 4, wait for gamemaster approval
      setFeedback({
        type: 'info',
        message: 'Answer submitted! Waiting for gamemaster approval...'
      });

      // Wait for gamemaster response
      // The response will come via socket event
      return;
    }

    setLoading(true);
    // For dining-room, cabinet is always null, for other sections use currentCabinet
    const cabinetParam = section === 'dining-room' ? null : currentCabinet;
    const result = await submitAnswer(section, currentQuestion._id, answer, cabinetParam);
    setLoading(false);

    if (result.error) {
      setFeedback({ type: 'error', message: result.error });
      return;
    }

    // Don't show correct/incorrect feedback - just update score silently
    setScore(result.sectionScore);

    // Immediately return to cabinets/question selection (no delay, no feedback)
    if (section === 'dining-room') {
      // Dining room: Move to next question immediately
      const currentQuestionOrder = currentQuestion?.order || 0;
      // Sort questions by order and find the next one
      const sortedQuestions = [...questions].sort((a, b) => (a.order || 0) - (b.order || 0));
      // Find the next question with order greater than current
      const nextQuestion = sortedQuestions.find(q => (q.order || 0) > currentQuestionOrder);

      if (nextQuestion) {
        setCurrentQuestion(nextQuestion);
        setAnswer('');
        setFeedback(null);
        setDiningRoomCompleted(false);
      } else {
        // All questions answered
        setDiningRoomCompleted(true);
        setCurrentQuestion(null);
        setAnswer('');
        setFeedback(null);
      }
    } else {
      // Other sections: Immediately return to cabinet selection
      setCurrentQuestion(null);
      setCurrentCabinet(null);
      setAnswer('');
      setFeedback(null);
    }
  };

  // Note: We don't reset isAudioPlaying when question changes
  // The audio playing state should only be controlled by the audio element's events
  // This ensures the button remains disabled if audio is still playing when user moves to next question

  // Handle audio play/replay for dining room (single button)
  const handleAudioAction = async () => {
    // Prevent action if audio is already playing
    if (isAudioPlaying) {
      return;
    }

    // Get the audio element first
    const audioElement = document.getElementById('dining-room-audio');
    if (!audioElement) {
      console.error('Audio element not found');
      setFeedback({
        type: 'error',
        message: 'Audio player not found. Please refresh the page.'
      });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    // First play: use Valmont 2.0 (free)
    if (!audioPlayed) {
      audioElement.src = DINING_ROOM_PLAY_AUDIO_URL;
      setAudioPlayed(true);
      const result = await requestAudioReplay(section);
      setAudioPlayCount(result.playCount || 1);
    } else {
      // Replay: use Valmont 2.1 (with penalty)
      audioElement.src = DINING_ROOM_REPLAY_AUDIO_URL;

      // Apply penalty for replay
      const result = await requestAudioReplay(section);
      if (result.penalty) {
        // Update local score with penalty
        setScore(prevScore => prevScore + result.penalty); // penalty is negative, so this subtracts

        setFeedback({
          type: 'penalty',
          message: `Audio replay penalty: ${result.penalty} points`,
          scoreChange: result.penalty
        });
        setTimeout(() => setFeedback(null), 2000);
      }
      setAudioPlayCount(result.playCount);
    }

    // Play the audio with error handling
    try {
      // Reset audio to beginning if needed
      audioElement.currentTime = 0;
      // Load the new source if it changed
      audioElement.load();
      // Play the audio
      await audioElement.play();
      // setIsAudioPlaying will be set by the onPlay handler on the audio element
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsAudioPlaying(false);
      setFeedback({
        type: 'error',
        message: 'Failed to play audio. Please try again.'
      });
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  const handleHelpRequest = () => {
    requestHelp(section);
    alert('Help request sent to gamemaster! They will be with you shortly.');
  };

  // Listen for dining room question 4 result
  useEffect(() => {
    if (section === 'dining-room' && socket) {
      const handleQuestion4Result = (result) => {
        // Check if this result is for our team
        if (result.teamId && selectedTeam && result.teamId.toString() !== selectedTeam.id.toString()) {
          return;
        }

        if (result.success && !result.pending) {
          // Don't show approved/rejected feedback - just update score silently
          setScore(result.sectionScore);

          // Immediately close question after any result (approved or rejected)
          // Question 4 is the last question, so always mark as completed
          setDiningRoomCompleted(true);
          setCurrentQuestion(null);
          setAnswer('');
          setFeedback(null);
        }
      };

      socket.on('dining-room:question4:result', handleQuestion4Result);

      return () => {
        socket.off('dining-room:question4:result', handleQuestion4Result);
      };
    }
  }, [section, socket, selectedTeam]);

  // Set current question for dining room on mount
  useEffect(() => {
    if (section === 'dining-room' && questions.length > 0 && !currentQuestion && !diningRoomCompleted) {
      // Get the first unanswered question for dining room
      const teamProgress = currentSession?.teamProgress?.find(
        tp => tp.teamId.toString() === selectedTeam?.id
      );
      const answeredQuestions = teamProgress?.sections['dining-room']?.answeredQuestions || [];

      // Find first unanswered question by order (questions are already sorted)
      const unansweredQuestion = questions.find(q => {
        const questionId = typeof q._id === 'string' ? q._id : q._id.toString();
        return !answeredQuestions.some(ansId => {
          const ansIdStr = typeof ansId === 'string' ? ansId : ansId.toString();
          return ansIdStr === questionId;
        });
      });

      if (unansweredQuestion) {
        setCurrentQuestion(unansweredQuestion);
        setDiningRoomCompleted(false);
      } else if (questions.length > 0) {
        // If all answered, show completion
        setDiningRoomCompleted(true);
        setCurrentQuestion(null);
      }
    }
  }, [section, questions, currentQuestion, currentSession, selectedTeam, diningRoomCompleted]);

  // Calculate answered cabinets map to avoid infinite re-renders
  const answeredCabinetsMap = useMemo(() => {
    const answeredMap = new Map();

    if (currentSession && selectedTeam && questions.length > 0) {
      const teamProgress = currentSession.teamProgress?.find(
        tp => tp.teamId.toString() === selectedTeam.id
      );

      if (teamProgress) {
        const sectionProgress = teamProgress.sections[section];
        const answeredQuestions = sectionProgress?.answeredQuestions || [];

        questions.forEach(question => {
          if (question.cabinet) {
            const questionId = typeof question._id === 'string' ? question._id : question._id.toString();
            const isAnswered = answeredQuestions.some(ansId => {
              const ansIdStr = typeof ansId === 'string' ? ansId : ansId.toString();
              return ansIdStr === questionId;
            });
            answeredMap.set(question.cabinet, isAnswered);
          }
        });
      }
    }

    return answeredMap;
  }, [currentSession, selectedTeam, section, questions]);

  // Dining Room Interface (Multiple Audio Questions)
  if (section === 'dining-room') {
    const question = currentQuestion;

    return (
      <div className={`game-interface dining-room ${feedback ? feedback.type + '-flash' : ''}`}>
        <div className="game-header">
          <div className="header-info">
            <h1>{sectionTitle}</h1>
            <p className="team-name">{selectedTeam?.name}</p>
          </div>
          <div className="score-display">
            <span className="score-label">Score:</span>
            <span className="score-value">{score}</span>
          </div>
        </div>

        <div className="game-content">
          {diningRoomCompleted ? (
            <div className="loading-screen">
              <div className="loading-content">
                <div className="loading-logo-container">
                  <img
                    src="/fortuin.gif"
                    alt="Escape Room Logo"
                    className="loading-logo-gif"
                  />
                </div>
                <h1>Preparing Next Game...</h1>
                <div className="spinner"></div>
                <p className="loading-message">
                  Waiting for gamemaster to start a new game...
                </p>
              </div>
            </div>
          ) : question ? (
            <div className="dining-room-layout">
              {/* Left Side - Question Section */}
              <div className="question-section">
                <div className="audio-question-card">
                  <div className="question-progress">
                    <span className="question-counter">
                      Question {question.order || (questions.findIndex(q => {
                        const qId = typeof q._id === 'string' ? q._id : q._id.toString();
                        const currentId = typeof question._id === 'string' ? question._id : question._id.toString();
                        return qId === currentId;
                      }) + 1)} of {questions.length}
                    </span>
                  </div>
                  <h2>{question.questionText}</h2>

                  <div className="answer-section">
                    <h3>Your Answer:</h3>
                    {question.order === 4 ? (
                      // Question 4: Input field instead of multiple choice
                      <div className="text-answer-section">
                        <input
                          type="text"
                          value={answer}
                          onChange={(e) => setAnswer(e.target.value)}
                          placeholder="Type your answer here..."
                          className="text-answer-input"
                          disabled={loading}
                        />
                        <button
                          onClick={handleSubmitAnswer}
                          className="btn-gold submit-btn"
                          disabled={!answer.trim() || loading || !currentQuestion}
                        >
                          {loading ? 'Submitting...' : 'Submit Answer'}
                        </button>
                      </div>
                    ) : (
                      // Questions 1-3: Multiple choice
                      <>
                        <div className="options-grid">
                          {question.options.map((option, index) => (
                            <button
                              key={index}
                              className={`option-button ${answer === option.text ? 'selected' : ''}`}
                              onClick={() => setAnswer(option.text)}
                              disabled={loading}
                            >
                              {option.text}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={handleSubmitAnswer}
                          className="btn-gold submit-btn"
                          disabled={!answer || loading || !currentQuestion}
                        >
                          {loading ? 'Submitting...' : 'Submit Answer'}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Only show error feedback, not correct/incorrect */}
                  {feedback && feedback.type === 'error' && (
                    <div className={`feedback-message ${feedback.type}`}>
                      {feedback.message}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Single Audio Section */}
              <div className="audio-files-section">
                <h3 className="audio-section-title">Audio Message</h3>
                <p className="audio-section-subtitle">
                  First play is free. Replays cost 20 points each.
                </p>
                <div className="audio-files-list">
                  <div className="audio-file-item">
                    <div className="audio-file-header">
                      <span className="audio-file-label">Dining Room Audio</span>
                      {audioPlayed && (
                        <span className="audio-played-badge">
                          🔊 Played
                        </span>
                      )}
                    </div>
                    <audio
                      id="dining-room-audio"
                      src={DINING_ROOM_PLAY_AUDIO_URL}
                      preload="metadata"
                      onPlay={() => setIsAudioPlaying(true)}
                      onPause={() => setIsAudioPlaying(false)}
                      onEnded={() => setIsAudioPlaying(false)}
                      onError={() => {
                        setIsAudioPlaying(false);
                        setFeedback({
                          type: 'error',
                          message: 'Failed to load audio file. Please refresh the page.'
                        });
                        setTimeout(() => setFeedback(null), 3000);
                      }}
                    >
                      Your browser does not support the audio element.
                    </audio>
                    <button
                      onClick={handleAudioAction}
                      className={`audio-action-btn ${audioPlayed ? 'replay-mode' : 'play-mode'}`}
                      disabled={isAudioPlaying}
                      title={isAudioPlaying ? 'Wait for audio to finish' : audioPlayed ? 'Replay (20 point penalty)' : 'Play (Free)'}
                    >
                      {audioPlayed ? (
                        <>
                          <span className="replay-icon">↻</span>
                          <span className="replay-text">Replay</span>
                        </>
                      ) : (
                        <>
                          <span className="play-icon">▶</span>
                          <span className="play-text">Play</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-questions">
              <p>No questions available for this section yet.</p>
            </div>
          )}
        </div>

        {/* <button onClick={handleHelpRequest} className="help-button">
          🆘 Request Help
        </button> */}
      </div>
    );
  }

  // Other Sections (Cabinet-based)
  if (!currentQuestion) {
    // Show cabinet selection
    const cabinets = [...new Set(questions.map(q => q.cabinet))].filter(Boolean);

    return (
      <div className="game-interface">
        <div className="game-header">
          <div className="header-info">
            <h1>{sectionTitle}</h1>
            <p className="team-name">{selectedTeam?.name}</p>
          </div>
          <div className="score-display">
            <span className="score-label">Score:</span>
            <span className="score-value">{score}</span>
          </div>
        </div>

        <div className="game-content">
          <h2 className="instruction">Select a Cabinet</h2>
          <div className="cabinets-grid">
            {cabinets.map((cabinet, index) => {
              // Check if this cabinet's question has been answered
              const isAnswered = answeredCabinetsMap.get(cabinet) || false;

              return (
                <button
                  key={index}
                  className={`cabinet-button ${isAnswered ? 'answered' : ''}`}
                  onClick={() => handleCabinetSelect(cabinet)}
                  disabled={isAnswered}
                >
                  <span className="cabinet-icon">🗄️</span>
                  <span className="cabinet-name">{cabinet}</span>
                  {isAnswered && <span className="answered-badge">✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* <button onClick={handleHelpRequest} className="help-button">
          🆘 Request Help
        </button> */}
      </div>
    );
  }

  // Show question
  return (
    <div className={`game-interface ${feedback ? feedback.type + '-flash' : ''}`}>
      <div className="game-header">
        <div className="header-info">
          <h1>{sectionTitle}</h1>
          <p className="team-name">{selectedTeam?.name}</p>
        </div>
        <div className="score-display">
          <span className="score-label">Score:</span>
          <span className="score-value">{score}</span>
        </div>
      </div>

      <div className="game-content">
        <div className="question-card">
          <h3 className="cabinet-label">{currentCabinet}</h3>
          <h2 className="question-text">{currentQuestion.questionText}</h2>

          <div className="answer-section">
            <h3>Select Your Answer:</h3>
            <div className="options-grid">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  className={`option-button ${answer === option.text ? 'selected' : ''}`}
                  onClick={() => setAnswer(option.text)}
                  disabled={loading}
                >
                  {option.text}
                </button>
              ))}
            </div>

            <div className="action-buttons">
              <button
                onClick={() => {
                  setCurrentQuestion(null);
                  setCurrentCabinet(null);
                  setAnswer('');
                  setFeedback(null);
                }}
                className="btn-gold back-btn"
              >
                Back to Cabinets
              </button>
              <button
                onClick={handleSubmitAnswer}
                className="btn-gold submit-btn"
                disabled={!answer || loading}
              >
                {loading ? 'Submitting...' : 'Submit Answer'}
              </button>
            </div>
          </div>

          {/* Feedback removed - players don't see if answer is correct/incorrect */}
          {feedback && feedback.type === 'error' && (
            <div className={`feedback-message ${feedback.type}`}>
              {feedback.message}
            </div>
          )}
        </div>
      </div>

      {/* <button onClick={handleHelpRequest} className="help-button">
        🆘 Request Help
      </button> */}
    </div>
  );
}

export default GameInterface;

